import { createElement, createRef } from "react";
import { createRoot } from "react-dom/client";

/**
 * This utility allows us to use React only for templating, but bypass it's render loop
 *
 * This wrapper allows us to create an element outside React, to where we create a `root`, so we can
 * imperatively render to it and then take the result from the element and replace the `ref`
 * content. This mechanism allows us to use React for templating inside `MarkerEditor` while still
 * bypassing the performance implication of doing all operations through React state.
 * `ReactDOM.render` used to allow something similar to this, but it's deprecated
 */
export function imperativeReactComponent<
  Tag extends keyof JSX.IntrinsicElements,
  Namespace extends Parameters<typeof document.createElementNS>[0],
>(
  element: Tag | [Namespace, Tag],
  key: string,
  props?: JSX.IntrinsicElements[Tag],
) {
  const createElementArgs: Parameters<typeof document.createElementNS> = !Array.isArray(element) ? ["http://www.w3.org/1999/xhtml", element] : element;
  const el = document.createElementNS(...createElementArgs);
  const root = createRoot(el);
  const ref = createRef<typeof el>();

  return {
    ...createElement(createElementArgs[1], { key, ref, ...(props ?? {}) }),
    update(children: React.ReactNode) {
      root.render(children);
      if (!ref.current) return;

      const attributes = [...ref.current.attributes];
      ref.current.replaceWith(el);
      attributes.forEach((attr: Attr) => {
        el.setAttribute(attr.name, attr.value);
      });
    },
  };
}
